import { usePolling } from "./usePolling";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  GuestServiceRequestDto,
  listGuestServiceRequests,
} from "../utils/guestServiceRequestsApi";
import { listBuildings } from "../utils/buildingsApi";
import {
  getSeenRoomServiceRequestIds,
  markRoomServiceRequestsSeen,
  publishRoomServiceAlert,
} from "../utils/roomServiceAlert";

// Every open admin tab polls; usePolling skips hidden tabs and never overlaps
// requests. 15 s keeps alerts prompt without loading the server.
const POLL_INTERVAL_MS = 15_000;

/** Polls for open (New) guest-service requests — Amenities/House/Help/
 * Travel, never Food (that's RestaurantPage's own popup/badge) — every few
 * seconds so the whole admin console reacts to a just-submitted request
 * quickly, without the Room Service page itself needing to be open. Mounted
 * once, in AdminLayout. Mirrors useRestaurantNewOrders exactly. */
export const useRoomServiceRequestAlerts = () => {
  const { user } = useAuth();
  const accountId = Number(user?.accountId) || 0;

  const [popupRequest, setPopupRequest] = useState<GuestServiceRequestDto | null>(
    null,
  );
  const knownIdsRef = useRef<Set<number> | null>(null);
  const poppedIdRef = useRef<number | null>(null);

  const [buildingNameById, setBuildingNameById] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    if (!accountId) return;
    listBuildings()
      .then((all) => {
        const map: Record<number, string> = {};
        all.forEach((b) => {
          if (b.buildingName) map[b.buildingId] = b.buildingName;
        });
        setBuildingNameById(map);
      })
      .catch(() => {
        // Popup just won't show a building name.
      });
  }, [accountId]);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      const pending = await listGuestServiceRequests({
        accountId,
        requestStatus: "New",
      });

      const seen = getSeenRoomServiceRequestIds();
      const unseen = pending.filter((r) => !seen.has(r.requestId));
      publishRoomServiceAlert(
        unseen.length,
        unseen.filter((r) => r.isUrgent).length,
      );

      const isFirstPoll = knownIdsRef.current === null;
      const known = knownIdsRef.current ?? new Set<number>();
      const arrivals = pending.filter(
        (r) => !known.has(r.requestId) && !seen.has(r.requestId),
      );
      knownIdsRef.current = new Set(pending.map((r) => r.requestId));

      if (!isFirstPoll && arrivals.length > 0) {
        const latest = arrivals.reduce((a, b) =>
          new Date(a.requestedOn).getTime() >= new Date(b.requestedOn).getTime()
            ? a
            : b,
        );
        if (poppedIdRef.current !== latest.requestId) {
          poppedIdRef.current = latest.requestId;
          setPopupRequest(latest);
        }
      }
    } catch {
      // Best-effort — a failed poll just tries again next tick.
    }
  }, [accountId]);

  usePolling(poll, POLL_INTERVAL_MS);

  const dismissPopup = useCallback(() => setPopupRequest(null), []);

  const acknowledgePopup = useCallback((requestId: number) => {
    markRoomServiceRequestsSeen([requestId]);
    setPopupRequest((current) =>
      current?.requestId === requestId ? null : current,
    );
    poll();
  }, [poll]);

  const buildingName = popupRequest?.buildingId
    ? buildingNameById[popupRequest.buildingId] ?? null
    : null;

  return { popupRequest, buildingName, dismissPopup, acknowledgePopup };
};
